import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Calculator, Activity, Download, Upload, FileSpreadsheet, FileText } from 'lucide-react';
import { useEmpresa } from '@/context/EmpresaContext';
import DonutChart from '@/components/DonutChart';
import DebtEvolutionCharts from '@/components/DebtEvolutionCharts';
import PDFReportGenerator from '@/components/PDFReportGenerator';
import MetricCard from '@/components/MetricCard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export default function Dashboard() {
  const { selectedEmpresa, API, refreshEmpresas } = useEmpresa();
  // Empresa compartida en modo solo lectura: puede ver y exportar, pero no importar datos
  const isReadOnly = selectedEmpresa?.my_role === 'read';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [proyeccionesAnuales, setProyeccionesAnuales] = useState([]);
  const [operacionesLP, setOperacionesLP] = useState([]);
  const [operacionesCP, setOperacionesCP] = useState([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedEmpresa) {
        setLoading(false);
        return;
      }
      try {
        // Cargar estadísticas del dashboard y operaciones
        const [statsRes, operacionesLPRes, operacionesCPRes] = await Promise.all([
          axios.get(`${API}/dashboard/${selectedEmpresa.id}`),
          axios.get(`${API}/operaciones-lp?empresa_id=${selectedEmpresa.id}`),
          axios.get(`${API}/operaciones-cp?empresa_id=${selectedEmpresa.id}`)
        ]);
        
        setStats(statsRes.data);
        setOperacionesLP(operacionesLPRes.data);
        setOperacionesCP(operacionesCPRes.data);
        
        // Calcular proyecciones para las gráficas de evolución
        const proyecciones = calcularProyeccionesAnuales(operacionesLPRes.data);
        setProyeccionesAnuales(proyecciones);
        
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        toast.error('Error al cargar las estadísticas');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [selectedEmpresa, API]);

  // Funciones para calcular proyecciones
  const mesesEntreFechas = (fechaInicio, fechaFin) => {
    const inicio = new Date(fechaInicio);
    const fin = new Date(fechaFin);
    return Math.round((fin - inicio) / (1000 * 60 * 60 * 24 * 30.44));
  };

  const cuotasPagadas = (fechaInicio) => {
    const inicio = new Date(fechaInicio);
    const hoy = new Date();
    const meses = Math.floor((hoy - inicio) / (1000 * 60 * 60 * 24 * 30.44));
    return Math.max(0, meses);
  };

  const calcularTipoInteres = (importeSolicitado, cuotaMensual, plazoTotalMeses) => {
    if (!importeSolicitado || !cuotaMensual || !plazoTotalMeses) return 0;
    const totalPagado = cuotaMensual * plazoTotalMeses;
    if (totalPagado <= importeSolicitado) return 0;
    
    let i = ((totalPagado - importeSolicitado) / importeSolicitado) / (plazoTotalMeses / 12) / 12;
    if (i <= 0) i = 0.005;
    
    for (let iter = 0; iter < 100; iter++) {
      const factor = Math.pow(1 + i, plazoTotalMeses);
      const cuotaCalc = importeSolicitado * (i * factor) / (factor - 1);
      const error = cuotaCalc - cuotaMensual;
      if (Math.abs(error) < 0.01) break;
      
      const h = 0.00001;
      const factor2 = Math.pow(1 + i + h, plazoTotalMeses);
      const cuotaCalc2 = importeSolicitado * ((i + h) * factor2) / (factor2 - 1);
      const derivada = (cuotaCalc2 - cuotaCalc) / h;
      if (Math.abs(derivada) < 0.0001) break;
      
      i = i - error / derivada;
      i = Math.max(0.0001, Math.min(0.05, i));
    }
    
    return i * 12 * 100;
  };

  const calcularProyeccionesAnuales = (operaciones) => {
    const hoy = new Date();
    const resumenPorAño = {};

    operaciones.forEach(op => {
      if (!op.capital_pendiente || op.capital_pendiente <= 0) return;
      if (!op.importe_solicitado || !op.cuota_mensual || !op.fecha_inicio || !op.fecha_fin) return;

      const plazoTotalMeses = mesesEntreFechas(op.fecha_inicio, op.fecha_fin);
      const mesesPagados = cuotasPagadas(op.fecha_inicio);
      const mesesRestantes = Math.max(1, plazoTotalMeses - mesesPagados);

      let tipoInteres = op.tipo_interes || 0;
      if (!tipoInteres) {
        tipoInteres = calcularTipoInteres(op.importe_solicitado, op.cuota_mensual, plazoTotalMeses);
      }

      const tipoInteresMensual = (tipoInteres / 100) / 12;
      let capitalRestante = op.capital_pendiente;
      
      // Calcular cuota fija
      let cuotaFija;
      if (tipoInteresMensual > 0) {
        const factor = Math.pow(1 + tipoInteresMensual, mesesRestantes);
        cuotaFija = op.capital_pendiente * (tipoInteresMensual * factor) / (factor - 1);
      } else {
        cuotaFija = op.capital_pendiente / mesesRestantes;
      }

      // Generar cuotas y agrupar por año
      let fechaCuota = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1);
      
      for (let i = 0; i < mesesRestantes; i++) {
        const interesMes = capitalRestante * tipoInteresMensual;
        let amortMes = cuotaFija - interesMes;
        if (i === mesesRestantes - 1) amortMes = capitalRestante;
        
        const año = fechaCuota.getFullYear();
        
        if (!resumenPorAño[año]) {
          resumenPorAño[año] = {
            año,
            totalAmortizacion: 0,
            totalInteres: 0
          };
        }
        
        resumenPorAño[año].totalAmortizacion += amortMes;
        resumenPorAño[año].totalInteres += interesMes;
        
        capitalRestante -= amortMes;
        fechaCuota.setMonth(fechaCuota.getMonth() + 1);
      }
    });

    return Object.values(resumenPorAño).sort((a, b) => a.año - b.año);
  };

  const handleExport = async () => {
    if (!selectedEmpresa) {
      toast.error('Selecciona una empresa primero');
      return;
    }
    try {
      const response = await axios.get(`${API}/export/${selectedEmpresa.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pool_bancario_${selectedEmpresa.nombre}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el Excel');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/export-template`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'plantilla_pool_bancario.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Plantilla descargada');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Error al descargar la plantilla');
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!selectedEmpresa) {
      toast.error('Selecciona una empresa primero');
      return;
    }

    // Guardar archivo y mostrar confirmación
    setPendingFile(file);
    setConfirmImportOpen(true);
  };

  const processImport = async () => {
    if (!pendingFile || !selectedEmpresa) return;
    
    setConfirmImportOpen(false);
    setImporting(true);
    
    try {
      // Primero eliminar operaciones existentes de la empresa
      const [existingLP, existingCP] = await Promise.all([
        axios.get(`${API}/operaciones-lp?empresa_id=${selectedEmpresa.id}`),
        axios.get(`${API}/operaciones-cp?empresa_id=${selectedEmpresa.id}`)
      ]);
      
      // Eliminar operaciones existentes
      for (const op of existingLP.data) {
        await axios.delete(`${API}/operaciones-lp/${op.id}`);
      }
      for (const op of existingCP.data) {
        await axios.delete(`${API}/operaciones-cp/${op.id}`);
      }
      
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true });
          
          let lpCount = 0;
          let cpCount = 0;
          
          // Helper to format Excel date - handles multiple formats including DD/MM/YYYY
          const formatDate = (val) => {
            if (!val) return '';
            
            // If it's a Date object
            if (val instanceof Date) {
              // Ajustar zona horaria para evitar desfase de un día
              const adjustedDate = new Date(val.getTime() + val.getTimezoneOffset() * 60000);
              const year = adjustedDate.getFullYear();
              const month = String(adjustedDate.getMonth() + 1).padStart(2, '0');
              const day = String(adjustedDate.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // If it's an Excel serial date (number)
            if (typeof val === 'number') {
              // Excel fecha serial: días desde 1900-01-01
              // Ajuste para el bug de Excel con 1900
              const excelEpoch = new Date(1899, 11, 30); // 30 Dec 1899
              const date = new Date(excelEpoch.getTime() + val * 86400 * 1000);
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // If it's a string
            const str = String(val).trim();
            
            // Formato DD/MM/YYYY o DD-MM-YYYY (español)
            const spanishMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
            if (spanishMatch) {
              const day = String(spanishMatch[1]).padStart(2, '0');
              const month = String(spanishMatch[2]).padStart(2, '0');
              const year = spanishMatch[3];
              return `${year}-${month}-${day}`;
            }
            
            // Formato YYYY-MM-DD o YYYY/MM/DD (ISO)
            const isoMatch = str.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/);
            if (isoMatch) {
              const year = isoMatch[1];
              const month = String(isoMatch[2]).padStart(2, '0');
              const day = String(isoMatch[3]).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
            
            // Intentar parsear como fecha genérica
            try {
              const parsed = new Date(str);
              if (!isNaN(parsed.getTime())) {
                const year = parsed.getFullYear();
                const month = String(parsed.getMonth() + 1).padStart(2, '0');
                const day = String(parsed.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              }
            } catch (e) {}
            
            return '';
          };

          // Helper to get value from multiple possible column names
          const getValue = (row, ...keys) => {
            for (const key of keys) {
              if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
                return row[key];
              }
            }
            return null;
          };
          
          // Check for single-sheet format (Pool Bancari style)
          const poolBancariSheet = workbook.Sheets['Pool Bancari'];
          if (poolBancariSheet) {
            // Parse the complex single-sheet format
            const allData = XLSX.utils.sheet_to_json(poolBancariSheet, { header: 1, defval: null });
            
            let inLPSection = false;
            let inCPSection = false;
            
            for (let i = 0; i < allData.length; i++) {
              const row = allData[i];
              if (!row) continue;
              
              // Detect section markers
              const firstCell = String(row[0] || '').toUpperCase();
              if (firstCell.includes('DEUTE LLARG') || firstCell.includes('LARGO PLAZO')) {
                inLPSection = true;
                inCPSection = false;
                continue;
              }
              if (firstCell.includes('DEUTE CURT') || firstCell.includes('CORTO PLAZO')) {
                inLPSection = false;
                inCPSection = true;
                continue;
              }
              
              // Skip header rows, empty rows, and sum rows
              const col3 = row[2]; // Column C (index)
              const col4 = row[3]; // Column D (entidad)
              if (!col3 || !col4 || String(col4).includes('Entitat') || String(col4).includes('ENTIDAD')) continue;
              if (String(row[5] || '').includes('SUM') || String(row[6] || '').includes('SUM')) continue;
              
              if (inLPSection && col4) {
                // Pool Bancari format:
                // Col D (3)=Entidad, E (4)=Tipo, F (5)=Importe, G (6)=Capital, H (7)=Cuota
                // Col J (9)=Carencia, K (10)=Duración(años), L (11)=FechaInicio
                const entidad = String(row[3] || '');
                const tipo = String(row[4] || 'Préstamo');
                const importe = parseFloat(row[5]) || 0;
                const capital = parseFloat(row[6]) || 0;
                const cuota = parseFloat(row[7]) || 0;
                const carenciaMeses = parseInt(row[8]) || 0;
                const duracionAnos = parseFloat(row[9]) || 0;
                const fechaInicioRaw = row[10];
                
                // Calcular fecha fin = fecha inicio + duración
                let fechaFin = '';
                const fechaInicio = formatDate(fechaInicioRaw);
                if (fechaInicio && duracionAnos > 0) {
                  try {
                    const startDate = new Date(fechaInicio);
                    startDate.setFullYear(startDate.getFullYear() + Math.floor(duracionAnos));
                    startDate.setMonth(startDate.getMonth() + Math.round((duracionAnos % 1) * 12));
                    fechaFin = startDate.toISOString().split('T')[0];
                  } catch (e) {
                    fechaFin = '';
                  }
                }
                
                const payload = {
                  empresa_id: selectedEmpresa.id,
                  entidad_financiera: entidad,
                  tipologia_producto: tipo,
                  importe_solicitado: importe,
                  capital_pendiente: capital,
                  cuota_mensual: cuota,
                  carencia: carenciaMeses,
                  tipo_interes: 0,
                  fecha_inicio: fechaInicio,
                  fecha_fin: fechaFin,
                  cirbe: true
                };
                
                if (payload.entidad_financiera && payload.importe_solicitado > 0) {
                  await axios.post(`${API}/operaciones-lp`, payload);
                  lpCount++;
                }
              }
              
              if (inCPSection && col4) {
                // CP: D=Entidad, E=Disponible(Limite), F=Dispuesto, G=Tipo
                const payload = {
                  empresa_id: selectedEmpresa.id,
                  entidad_financiera: String(row[3] || ''),
                  tipologia_producto: String(row[6] || 'Póliza de crédito'),
                  limite: parseFloat(row[4]) || 0,
                  dispuesto: parseFloat(row[5]) || 0,
                  coste_financiero: 0,
                  coste_disposicion: 0,
                  fecha_renovacion: ''
                };
                
                if (payload.entidad_financiera && payload.limite > 0) {
                  await axios.post(`${API}/operaciones-cp`, payload);
                  cpCount++;
                }
              }
            }
            
            toast.success(`Importado: ${lpCount} operaciones LP, ${cpCount} operaciones CP`);
            setImportDialogOpen(false);
            const response = await axios.get(`${API}/dashboard/${selectedEmpresa.id}`);
            setStats(response.data);
            return;
          }
          
          // Standard multi-sheet format
          // Find LP sheet (multiple possible names)
          const lpSheetNames = ['LARGO PLAZO', 'DEUTE LLARG', 'LP', 'Largo Plazo'];
          let lpSheet = null;
          for (const name of lpSheetNames) {
            if (workbook.Sheets[name]) {
              lpSheet = workbook.Sheets[name];
              break;
            }
          }
          
          if (lpSheet) {
            // Read as array to handle headers on row 4
            const lpRaw = XLSX.utils.sheet_to_json(lpSheet, { header: 1, defval: null });
            
            // Find header row (look for TIPOLOGIA or similar)
            let headerRowIdx = 0;
            for (let i = 0; i < Math.min(10, lpRaw.length); i++) {
              const row = lpRaw[i];
              if (row && row.some(cell => String(cell || '').includes('TIPOLOGIA') || String(cell || '').includes('ENTIDAD'))) {
                headerRowIdx = i;
                break;
              }
            }
            
            // Get headers and data
            const headers = lpRaw[headerRowIdx] || [];
            const dataRows = lpRaw.slice(headerRowIdx + 1);
            
            for (const row of dataRows) {
              if (!row || row.every(cell => cell === null || cell === '')) continue;
              
              // Map by header position
              const getCol = (headerName) => {
                const idx = headers.findIndex(h => String(h || '').toUpperCase().includes(headerName.toUpperCase()));
                return idx >= 0 ? row[idx] : null;
              };
              
              const tipologia = getCol('TIPOLOGIA') || getCol('TIPO');
              const entidad = getCol('ENTIDAD');
              const importe = getCol('IMPORTE');
              const capital = getCol('CAPITAL');
              const cuota = getCol('CUOTA');
              const carencia = getCol('CARENCIA');
              const tipoInteres = getCol('INTERES') || getCol('TIPO DE INTERES');
              
              // Buscar fecha inicio y fin por nombre exacto
              const idxFechaInicio = headers.findIndex(h => String(h || '').toUpperCase() === 'FECHA INICIO');
              const idxFechaFin = headers.findIndex(h => String(h || '').toUpperCase() === 'FECHA FIN');
              const fechaInicio = idxFechaInicio >= 0 ? row[idxFechaInicio] : null;
              const fechaFin = idxFechaFin >= 0 ? row[idxFechaFin] : null;
              
              const cirbe = getCol('CIRBE');
              
              const payload = {
                empresa_id: selectedEmpresa.id,
                tipologia_producto: String(tipologia || ''),
                entidad_financiera: String(entidad || ''),
                importe_solicitado: parseFloat(importe) || 0,
                capital_pendiente: parseFloat(capital) || 0,
                cuota_mensual: parseFloat(cuota) || 0,
                carencia: parseInt(carencia) || 0,
                tipo_interes: parseFloat(tipoInteres) || 0,
                fecha_inicio: formatDate(fechaInicio),
                fecha_fin: formatDate(fechaFin),
                cirbe: cirbe === 'Sí' || cirbe === 'Si' || cirbe === true || cirbe === 1
              };
              
              if (payload.entidad_financiera && (payload.importe_solicitado > 0 || payload.capital_pendiente > 0)) {
                await axios.post(`${API}/operaciones-lp`, payload);
                lpCount++;
              }
            }
          }
          
          // Find CP sheet (multiple possible names)
          const cpSheetNames = ['CORTO PLAZO', 'DEUTE CURT', 'CP', 'Corto Plazo'];
          let cpSheet = null;
          for (const name of cpSheetNames) {
            if (workbook.Sheets[name]) {
              cpSheet = workbook.Sheets[name];
              break;
            }
          }
          
          if (cpSheet) {
            // Read as array to handle headers on row 4
            const cpRaw = XLSX.utils.sheet_to_json(cpSheet, { header: 1, defval: null });
            
            // Find header row
            let headerRowIdx = 0;
            for (let i = 0; i < Math.min(10, cpRaw.length); i++) {
              const row = cpRaw[i];
              if (row && row.some(cell => String(cell || '').includes('TIPOLOGIA') || String(cell || '').includes('LIMITE'))) {
                headerRowIdx = i;
                break;
              }
            }
            
            // Get headers and data
            const headers = cpRaw[headerRowIdx] || [];
            const dataRows = cpRaw.slice(headerRowIdx + 1);
            
            for (const row of dataRows) {
              if (!row || row.every(cell => cell === null || cell === '')) continue;
              
              // Map by header position
              const getCol = (headerName) => {
                const idx = headers.findIndex(h => String(h || '').toUpperCase().includes(headerName.toUpperCase()));
                return idx >= 0 ? row[idx] : null;
              };
              
              const tipologia = getCol('TIPOLOGIA') || getCol('TIPO');
              const entidad = getCol('ENTIDAD') || getCol('FINANCIERA');
              const limite = getCol('LIMITE');
              const dispuesto = getCol('DISPUESTO');
              const costeFin = getCol('FINANCIERO');
              const costeDisp = getCol('DISPOSICION');
              const fechaRenov = getCol('RENOVACION') || getCol('FECHA');
              
              const payload = {
                empresa_id: selectedEmpresa.id,
                tipologia_producto: String(tipologia || ''),
                entidad_financiera: String(entidad || ''),
                limite: parseFloat(limite) || 0,
                dispuesto: parseFloat(dispuesto) || 0,
                coste_financiero: parseFloat(costeFin) || 0,
                coste_disposicion: parseFloat(costeDisp) || 0,
                fecha_renovacion: formatDate(fechaRenov)
              };
              
              if (payload.entidad_financiera && payload.limite > 0) {
                await axios.post(`${API}/operaciones-cp`, payload);
                cpCount++;
              }
            }
          }
          
          if (!lpSheet && !cpSheet) {
            toast.error('No se encontraron hojas válidas. Usa nombres: LARGO PLAZO / CORTO PLAZO o Pool Bancari');
          } else {
            toast.success(`Importado: ${lpCount} operaciones LP, ${cpCount} operaciones CP`);
          }
          setImportDialogOpen(false);
          
          // Refresh dashboard
          const response = await axios.get(`${API}/dashboard/${selectedEmpresa.id}`);
          setStats(response.data);
          
        } catch (error) {
          console.error('Error processing file:', error);
          toast.error('Error al procesar el archivo Excel');
        } finally {
          setImporting(false);
          setPendingFile(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      };
      reader.readAsArrayBuffer(pendingFile);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Error al leer el archivo');
      setImporting(false);
      setPendingFile(null);
    }
  };

  const cancelImport = () => {
    setConfirmImportOpen(false);
    setPendingFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!selectedEmpresa) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center" data-testid="no-empresa-message">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Activity className="w-10 h-10 text-slate-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 font-['Outfit'] mb-2">
          Sin empresa seleccionada
        </h2>
        <p className="text-slate-500 max-w-md">
          Crea una empresa en la sección "Empresas" o selecciona una existente para ver el dashboard.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Outfit']">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Pool bancario de <span className="font-medium text-slate-700">{selectedEmpresa.nombre}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={handleDownloadTemplate} 
            variant="outline"
            className="flex items-center gap-2"
            data-testid="download-template-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Descargar Plantilla
          </Button>
          {!isReadOnly && (
            <Button
              onClick={() => setImportDialogOpen(true)}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="import-excel-btn"
            >
              <Upload className="w-4 h-4" />
              Importar Excel
            </Button>
          )}
          <Button 
            onClick={handleExport} 
            className="btn-primary flex items-center gap-2"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4" />
            Exportar Excel
          </Button>
          <Button 
            onClick={() => setPdfDialogOpen(true)} 
            className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            data-testid="generate-pdf-btn"
          >
            <FileText className="w-4 h-4" />
            Informe PDF
          </Button>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Capital Pendiente L/P"
          value={stats?.capital_pendiente_lp || 0}
          icon={TrendingUp}
          color="amber"
        />
        <MetricCard
          title="Dispuesto C/P"
          value={stats?.dispuesto_cp || 0}
          icon={TrendingDown}
          color="blue"
        />
        <MetricCard
          title={`Cuota Anual Total ${proyeccionesAnuales.length > 0 ? proyeccionesAnuales[0].año : new Date().getFullYear()}`}
          value={proyeccionesAnuales.length > 0 
            ? proyeccionesAnuales[0].totalAmortizacion + proyeccionesAnuales[0].totalInteres 
            : 0}
          icon={Calculator}
          color="green"
        />
        <MetricCard
          title="Operaciones Activas"
          value={stats?.operaciones_activas || 0}
          icon={Activity}
          isCurrency={false}
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <DonutChart
          data={stats?.distribucion_lp_cp || []}
          title="Largo Plazo vs Corto Plazo"
        />
        <DonutChart
          data={stats?.composicion_producto || []}
          title="Composición por Producto"
        />
        <DonutChart
          data={stats?.distribucion_entidades || []}
          title="Entidades Bancarias"
        />
      </div>

      {/* Evolución de la Deuda */}
      {proyeccionesAnuales.length > 0 && (
        <DebtEvolutionCharts 
          proyeccionesData={proyeccionesAnuales} 
          capitalPendienteInicial={stats?.capital_pendiente_lp || 0}
        />
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Importar desde Excel</DialogTitle>
            <DialogDescription>
              Importa operaciones desde un archivo Excel. Usa la plantilla para asegurar el formato correcto.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Button 
              variant="outline" 
              className="w-full flex items-center justify-center gap-2"
              onClick={handleDownloadTemplate}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Descargar Plantilla Excel
            </Button>
            
            <div className="border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-600 mb-3">
                Selecciona el archivo Excel a importar:
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-medium
                  file:bg-amber-50 file:text-amber-700
                  hover:file:bg-amber-100
                  cursor-pointer"
                data-testid="file-input"
              />
            </div>
            
            {importing && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500"></div>
                Importando datos...
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Import Dialog */}
      <Dialog open={confirmImportOpen} onOpenChange={setConfirmImportOpen}>
        <DialogContent className="max-w-md" data-testid="confirm-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-amber-600">⚠️ Confirmar importación</DialogTitle>
            <DialogDescription className="text-slate-700 pt-2">
              <strong>Atención:</strong> Esta acción eliminará todas las operaciones existentes de <span className="font-semibold">{selectedEmpresa?.nombre}</span> y las sustituirá por los datos del archivo Excel.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
            <p className="text-sm text-amber-800">
              • Se eliminarán <strong>todas las operaciones a largo plazo</strong> de esta empresa<br/>
              • Se eliminarán <strong>todas las operaciones a corto plazo</strong> de esta empresa<br/>
              • Los nuevos datos del Excel reemplazarán los anteriores
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelImport}>
              Cancelar
            </Button>
            <Button 
              className="bg-amber-500 hover:bg-amber-600 text-slate-900"
              onClick={processImport}
            >
              Sí, sustituir datos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Report Generator */}
      <PDFReportGenerator
        empresa={selectedEmpresa}
        stats={stats}
        operacionesLP={operacionesLP}
        operacionesCP={operacionesCP}
        proyeccionesAnuales={proyeccionesAnuales}
        isOpen={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
      />
    </div>
  );
}
